import { useCallback, useMemo } from 'react';

import { alpha, Box, Button, Divider, makeStyles } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import LaunchOutlinedIcon from '@material-ui/icons/LaunchOutlined';
import PhoneIcon from '@material-ui/icons/Phone';
import ReplyIcon from '@material-ui/icons/Reply';
import copy from 'copy-to-clipboard';
import { useSnackbar } from 'notistack';

import { WppButtonsTypeEnum, WppButtonType } from 'store/reducers/templates';

type Props = {
  buttons?: WppButtonType[];
  showFully?: boolean;
  onShowMore?: (buttons: WppButtonType[]) => void;
};

const useStyles = makeStyles(({ palette }) => ({
  button: {
    color: 'inherit',
  },
  divider: {
    marginLeft: -16,
    marginRight: -16,
    backgroundColor: `${alpha(palette.text.secondary, 0.3)}`,
  },
  lastButton: {
    marginBottom: -6, // 2px faz toda diferença
  },
}));

const getButtonIcon = (type: WppButtonType['type']) => {
  switch (type) {
    case WppButtonsTypeEnum.URL:
      return <LaunchOutlinedIcon />;
    case WppButtonsTypeEnum.PHONE_NUMBER:
      return <PhoneIcon />;
    case WppButtonsTypeEnum.COPY_CODE:
      return <FileCopyIcon />;
    case WppButtonsTypeEnum.QUICK_REPLY:
      return <ReplyIcon />;
    default:
      return <PhoneIcon />;
  }
};

export default function TemplateButtons({
  buttons = [],
  showFully = false,
  onShowMore,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const shouldShowSeeAll = !showFully && buttons.length > 3;

  const visibleButtons = useMemo(() => {
    if (showFully || buttons.length <= 3) return buttons;
    return buttons.slice(0, 2);
  }, [buttons, showFully]);

  const handleClickButton = useCallback(
    (button: WppButtonType) => {
      if (button.type === WppButtonsTypeEnum.COPY_CODE) {
        copy(button?.example?.[0] || '');
        enqueueSnackbar('Texto copiado para a área de transferência.');
      } else if (button.type === WppButtonsTypeEnum.URL && button.url) {
        window.open(button.url, '_blank');
      }
    },
    [enqueueSnackbar],
  );

  if (!buttons.length) return null;

  return (
    <Box>
      <Divider className={classes.divider} />

      {visibleButtons.map((button, index) => (
        <Box key={`${button.type}-${button.text || index}`}>
          <Box
            p={0.2}
            className={
              !shouldShowSeeAll && index === visibleButtons.length - 1
                ? classes.lastButton
                : ''
            }
          >
            <Button
              className={classes.button}
              fullWidth
              size="small"
              startIcon={getButtonIcon(button.type)}
              onClick={() => handleClickButton(button)}
            >
              {button.text}
            </Button>
          </Box>
          {index < visibleButtons.length - 1 && (
            <Divider className={classes.divider} />
          )}
        </Box>
      ))}

      {shouldShowSeeAll && (
        <Box>
          <Divider className={classes.divider} />

          <Box p={0.2} className={classes.lastButton}>
            <Button
              className={classes.button}
              fullWidth
              size="small"
              startIcon={<FormatListBulletedIcon />}
              onClick={() => onShowMore?.(buttons)}
            >
              Ver todas as opções
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
